import { createSlice } from '@reduxjs/toolkit';
import {
  FAVORITE_STOPS_STORAGE_KEY,
  CENTER_TRACKED_VEHICLE_STORAGE_KEY,
} from '#/transit-tracker/constants';
import { getItem } from '#/shared/utils/storage';

/**
 * TODO: (PUBWEB-1796) Convert this module/slice to TypeScript
 *
 * This docstring provides temporary partial typing for this slice during
 * TypeScript migration. Only the properties currently required by TS consumers
 * are typed here. Additional properties can be added as needed
 *
 * @type {{
 *   alertActive: boolean,
 *   centerTrackedVehicleOnMap: boolean,
 *   favoriteStops: import('#/transit-tracker/types').FavoriteStop[],
 *   focusedOnHighlightedStop: boolean,
 *   highlightedStop: import('#/transit-tracker/types').HighlightedStop | null,
 * }}
 */
const initialState = {
  alertActive: false,
  centerTrackedVehicleOnMap: getItem(CENTER_TRACKED_VEHICLE_STORAGE_KEY, true),
  favoriteStops: getItem(FAVORITE_STOPS_STORAGE_KEY, []),
  focusedOnHighlightedStop: false,
  highlightedStop: null,
  trackedVehicle: null,
  vehicleRoute: null,
  vehicleRouteError: false,
  vehicleRouteLoading: false,
  vehicleRouteTimestamp: null,
};

const transitTrackerSlice = createSlice({
  name: 'transitTracker',
  initialState,
  reducers: {
    setCenterTrackedVehicleOnMap(state, action) {
      state.centerTrackedVehicleOnMap = action.payload;
    },
    fetchArrivalsError(state) {
      return {
        ...state,
        isArrivalsError: true,
      };
    },
    setHighlightedStop(state, action) {
      state.highlightedStop = action.payload;
    },
    setFocusedOnHighlightedStop(state, action) {
      state.focusedOnHighlightedStop = action.payload;
    },
    fetchVehicleRouteSuccess(state, action) {
      return {
        ...state,
        vehicleRouteTimestamp: Date.now(),
        vehicleRoute: action.payload,
        vehicleRouteLoading: false,
        isArrivalsError: false,
      };
    },
    fetchVehicleRouteEmpty(state) {
      return {
        ...state,
        vehicleRoute: null,
        vehicleRouteLoading: false,
      };
    },
    vehicleRouteCleared(state) {
      return {
        ...state,
        vehicleRoute: null,
        vehicleRouteLoading: false,
      };
    },
    favoriteStopsChanged(state, action) {
      state.favoriteStops = action.payload;
    },
    setTrackedVehicle(state, action) {
      return {
        ...state,
        trackedVehicle: action.payload,
      };
    },
    clearTrackedVehicle(state) {
      return {
        ...state,
        trackedVehicle: null,
      };
    },
  },
});

export const {
  setCenterTrackedVehicleOnMap,
  fetchArrivalsError,
  setHighlightedStop,
  setFocusedOnHighlightedStop,
  fetchVehicleRouteSuccess,
  fetchVehicleRouteEmpty,
  vehicleRouteCleared,
  favoriteStopsChanged,
  setTrackedVehicle,
  clearTrackedVehicle,
} = transitTrackerSlice.actions;

export const selectTrackedVehicle = state =>
  state.transitTracker.trackedVehicle;

export default transitTrackerSlice.reducer;
